import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { QRCodeSVG } from "qrcode.react";
import { ReactLenis } from "lenis/react";
import {
  Calendar,
  Clock,
  MapPin,
  ParkingCircle,
  Coffee,
  Rocket,
  Cpu,
  Satellite,
  Globe2,
  Users,
  ArrowRight,
  ArrowLeft,
  ScanLine,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { getEventBySlug } from "@/data/eventsData";

const PILLAR_ICONS = {
  Satellites: Satellite,
  "AI & Data": Cpu,
  "Earth Impact": Globe2,
  "Future Innovators": Users,
};

const HIGHLIGHT_ICONS = {
  "Real Satellites": Satellite,
  "Live Earth Data": Globe2,
  "Hands-on AI": Cpu,
  "Build the Future": Rocket,
};

export default function EventDetail() {
  const { slug } = useParams();
  const event = getEventBySlug(slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!event) return <Navigate to="/events" replace />;

  const isUpcoming = event.status === "upcoming";

  return (
    <ReactLenis root options={{ lerp: 0.09, smoothWheel: true }}>
      <div className="relative min-h-screen bg-arjuna-ink" data-testid="event-detail-page">
        <Navbar />

        {/* Hero */}
        <section className="relative overflow-hidden pt-28 pb-12">
          <div className="pointer-events-none absolute inset-0 opacity-70 [background:radial-gradient(60%_55%_at_20%_20%,rgba(0,229,255,0.14),transparent_60%),radial-gradient(50%_45%_at_85%_30%,rgba(255,122,26,0.10),transparent_60%)]" />
          <div className="container-x relative">
            <Link
              to="/events"
              data-testid="event-back-link"
              className="mb-6 inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.24em] text-slate-400 transition-colors hover:text-arjuna-teal"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> All Events
            </Link>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="max-w-3xl"
            >
              <span
                className="inline-flex items-center gap-2 rounded-full border px-3 py-1 font-mono text-[11px] uppercase tracking-[0.28em]"
                style={{
                  borderColor: isUpcoming ? "rgba(0,229,255,0.35)" : "rgba(255,255,255,0.2)",
                  color: isUpcoming ? "#00E5FF" : "#94A3B8",
                  background: isUpcoming ? "rgba(0,229,255,0.05)" : "rgba(255,255,255,0.03)",
                }}
              >
                <Rocket className="h-3.5 w-3.5" /> {isUpcoming ? "Upcoming" : "Past"} · {event.dateLabel.split(",").slice(-1)[0].trim()}
              </span>
              <h1 className="mt-6 font-serif text-4xl leading-[1.05] text-white sm:text-5xl lg:text-6xl">
                {event.title}
              </h1>
              {event.tagline && (
                <p className="mt-3 font-mono text-xs uppercase tracking-[0.28em] text-arjuna-teal">
                  {event.tagline}
                </p>
              )}
              <p className="mt-5 max-w-2xl text-base text-slate-300 sm:text-lg">
                {event.summary}
              </p>

              <div className="mt-8 flex flex-wrap items-center gap-3">
                {isUpcoming && (
                  <a
                    href={event.registerUrl}
                    target="_blank"
                    rel="noreferrer noopener"
                    data-testid="event-register-cta"
                    className="btn-primary group inline-flex items-center gap-2"
                  >
                    Register for the Event
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                  </a>
                )}
                <a
                  href="#event-poster"
                  data-testid="event-view-poster"
                  className="btn-ghost inline-flex items-center gap-2"
                >
                  View Poster
                </a>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Poster */}
        <section id="event-poster" className="relative pb-20">
          <div className="container-x">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="relative mx-auto max-w-4xl overflow-hidden rounded-3xl border border-white/10 bg-white/[0.02] p-3 shadow-[0_30px_80px_-30px_rgba(0,229,255,0.35)] backdrop-blur-md"
              data-testid="event-poster-frame"
            >
              <div className="absolute -inset-px rounded-3xl bg-gradient-to-br from-arjuna-teal/25 via-transparent to-[#FF7A1A]/20 opacity-70 blur-2xl" />
              <img
                src={event.posterUrl}
                alt={`${event.title} poster`}
                className="relative w-full rounded-2xl"
                data-testid="event-poster-image"
              />
            </motion.div>
          </div>
        </section>

        {/* Details Grid */}
        <section className="relative border-t border-white/10 bg-arjuna-surface/40 py-20">
          <div className="container-x">
            <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
              {/* Left */}
              <div className="space-y-6 lg:col-span-7">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <DetailCard
                    icon={Calendar}
                    label="Date"
                    lines={[event.dateLabel.split(",")[0], event.dateLabel.split(",").slice(1).join(",").trim()]}
                    accent="#FF7A1A"
                    testid="event-detail-date"
                  />
                  {event.time && (
                    <DetailCard
                      icon={Clock}
                      label="Main Event"
                      lines={[event.time, "All Ages"]}
                      accent="#00E5FF"
                      testid="event-detail-main"
                    />
                  )}
                  {event.workshop && (
                    <DetailCard
                      icon={Rocket}
                      label="Workshop"
                      lines={event.workshop.split("·").map((s) => s.trim())}
                      accent="#22C55E"
                      testid="event-detail-workshop"
                    />
                  )}
                  <DetailCard
                    icon={MapPin}
                    label="Venue"
                    lines={[event.venue, event.address]}
                    accent="#00E5FF"
                    testid="event-detail-venue"
                  />
                </div>

                {event.pillars?.length > 0 && (
                  <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6">
                    <h3 className="overline mb-4 text-arjuna-teal">What to Expect</h3>
                    <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                      {event.pillars.map((p) => {
                        const Icon = PILLAR_ICONS[p.title] || Satellite;
                        return (
                          <div key={p.title} className="rounded-xl border border-white/5 bg-black/30 p-4">
                            <Icon className="mb-3 h-6 w-6 text-arjuna-teal" />
                            <p className="font-mono text-[11px] uppercase tracking-widest text-white">
                              {p.title}
                            </p>
                            <p className="mt-1 text-xs leading-relaxed text-slate-400">{p.desc}</p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {event.highlights?.length > 0 && (
                  <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                    {event.highlights.map((h) => {
                      const Icon = HIGHLIGHT_ICONS[h] || Satellite;
                      return (
                        <div
                          key={h}
                          className="flex items-center gap-3 rounded-xl border border-arjuna-teal/20 bg-arjuna-teal/5 p-3"
                        >
                          <Icon className="h-5 w-5 text-arjuna-teal" />
                          <span className="text-xs font-medium text-slate-200">{h}</span>
                        </div>
                      );
                    })}
                  </div>
                )}

                <div className="flex flex-wrap gap-4 text-xs text-slate-400">
                  <span className="inline-flex items-center gap-2">
                    <ParkingCircle className="h-4 w-4 text-arjuna-teal" /> Ample parking
                    available
                  </span>
                  <span className="inline-flex items-center gap-2">
                    <Coffee className="h-4 w-4 text-arjuna-teal" /> Refreshments will be
                    provided
                  </span>
                </div>
              </div>

              {/* Right. Register */}
              <div className="lg:col-span-5">
                <motion.div
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                  className="sticky top-28 rounded-3xl border border-white/10 bg-gradient-to-br from-arjuna-surface to-black/60 p-6 shadow-[0_30px_80px_-40px_rgba(0,229,255,0.5)]"
                  data-testid="event-register-card"
                >
                  {isUpcoming ? (
                    <>
                      <div className="flex items-center gap-2">
                        <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
                        <span className="font-mono text-[10px] uppercase tracking-widest text-emerald-400">
                          Registrations Open
                        </span>
                      </div>
                      <h3 className="mt-3 font-serif text-2xl text-white">
                        Register by <span className="text-[#FF7A1A]">{event.registerBy}</span>
                      </h3>
                      <p className="mt-1 text-sm text-slate-400">
                        Scan the QR or tap the button. you'll be redirected to our
                        official registration form.
                      </p>

                      <div
                        className="mt-6 rounded-2xl border border-arjuna-teal/25 bg-white p-4"
                        data-testid="event-qr-wrapper"
                      >
                        <a
                          href={event.registerUrl}
                          target="_blank"
                          rel="noreferrer noopener"
                          aria-label="Open registration form"
                          className="mx-auto block w-fit"
                        >
                          <QRCodeSVG
                            value={event.registerUrl}
                            size={220}
                            level="H"
                            includeMargin={false}
                            bgColor="#FFFFFF"
                            fgColor="#031421"
                          />
                        </a>
                      </div>

                      <p className="mt-3 flex items-center justify-center gap-2 font-mono text-[10px] uppercase tracking-widest text-slate-400">
                        <ScanLine className="h-3.5 w-3.5 text-arjuna-teal" /> Scan to register
                      </p>

                      <a
                        href={event.registerUrl}
                        target="_blank"
                        rel="noreferrer noopener"
                        data-testid="event-register-btn"
                        className="btn-primary mt-6 inline-flex w-full items-center justify-center gap-2"
                      >
                        Register Now
                        <ArrowRight className="h-4 w-4" />
                      </a>
                    </>
                  ) : (
                    <>
                      <div className="flex items-center gap-2">
                        <span className="h-2 w-2 rounded-full bg-slate-500" />
                        <span className="font-mono text-[10px] uppercase tracking-widest text-slate-400">
                          Event Concluded
                        </span>
                      </div>
                      <h3 className="mt-3 font-serif text-2xl text-white">
                        Thanks for building with us.
                      </h3>
                      <p className="mt-1 text-sm text-slate-400">
                        Missed this one? Follow us for the next meetup or workshop.
                      </p>
                      <Link
                        to="/events"
                        className="btn-primary mt-6 inline-flex w-full items-center justify-center gap-2"
                      >
                        Explore All Events
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    </>
                  )}

                  <p className="mt-4 text-center text-[11px] text-slate-500">
                    Building space-based solutions for a{" "}
                    <span className="text-emerald-400">better Earth</span>.
                  </p>
                </motion.div>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </ReactLenis>
  );
}

function DetailCard({ icon: Icon, label, lines, accent, testid }) {
  return (
    <div
      className="group relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] p-5 transition-colors hover:border-arjuna-teal/40"
      data-testid={testid}
    >
      <div
        className="absolute -right-6 -top-6 h-24 w-24 rounded-full opacity-20 blur-2xl transition-opacity group-hover:opacity-40"
        style={{ background: accent }}
      />
      <Icon className="h-5 w-5" style={{ color: accent }} />
      <p className="mt-3 font-mono text-[10px] uppercase tracking-widest text-slate-400">
        {label}
      </p>
      <div className="mt-1 space-y-0.5">
        {lines.filter(Boolean).map((l, i) => (
          <p
            key={i}
            className={i === 0 ? "text-base font-semibold text-white" : "text-xs text-slate-400"}
          >
            {l}
          </p>
        ))}
      </div>
    </div>
  );
}
